import type { ListItemBlock, SlateBlock } from '@atlas/renderer';
import { Editor, Element, Transforms } from 'slate';
import isCalloutBlock from '@atlas/tool/box/slate/isCalloutBlock';
import isListBlock, { type ListBlock } from '@atlas/tool/box/slate/isListBlock';
import isTextBlock from '@atlas/tool/box/slate/isTextBlock';
import matchTopBlockNodes from '@atlas/tool/box/slate/matchTopBlockNodes';
import removeTopLevelBlocks from '@atlas/tool/box/slate/removeTopLevelBlocks';
import sortSelection from '@atlas/tool/box/slate/sortSelection';
import changeEditorSelection from '@atlas/tool/box/slate/changeEditorSelection';

export default function convertBlocksToLists(
    editor: Editor,
    blockType: ListBlock['type'],
    options?: {
        match?: SlateBlock['type'] | ((node: SlateBlock) => boolean);
    },
) {
    if (!editor.selection) return;

    const { anchor, focus } = editor.selection;
    const [selectionStart, selectionEnd] = sortSelection({ anchor, focus });

    Transforms.setNodes(
        editor,
        { type: blockType },
        {
            match: (node) => Element.isElement(node) && isListBlock(node),
            mode: 'all',
            at: {
                anchor: Editor.start(editor, [anchor.path[0]]),
                focus: Editor.end(editor, [focus.path[0]]),
            },
        },
    );

    const match = options?.match;
    const matches = matchTopBlockNodes(editor);

    const allBlocks = matches.reduce<SlateBlock[]>((allBlocks, [block]) => {
        if (typeof match === 'string' && match !== block.type) {
            return allBlocks.concat(block);
        }

        if (typeof match === 'function' && !match(block)) {
            return allBlocks.concat(block);
        }

        let listItems: Array<ListItemBlock | ListBlock> | null = null;

        if (isCalloutBlock(block)) {
            block = block.children[1];
        }

        if (isTextBlock(block)) {
            listItems = [{ type: 'list-item', children: block.children }];
        }

        if (isListBlock(block)) {
            listItems = block.children;
        }

        if (!listItems) return allBlocks.concat(block);

        const lastBlock = allBlocks[allBlocks.length - 1];
        const isLastBlockList = lastBlock && isListBlock(lastBlock);

        if (!isLastBlockList) {
            return allBlocks.concat({ type: blockType, children: listItems });
        }

        allBlocks[allBlocks.length - 1] = {
            ...lastBlock,
            children: lastBlock.children.concat(listItems),
        };

        return allBlocks;
    }, []);

    removeTopLevelBlocks(editor, selectionStart.path[0], selectionEnd.path[0]);
    Transforms.insertNodes(editor, allBlocks, { at: [selectionStart.path[0]] });

    const newSelectionStart = Editor.start(editor, [selectionStart.path[0]]);

    const newSelectionEnd = Editor.end(editor, [selectionStart.path[0] + allBlocks.length - 1]);

    changeEditorSelection(editor, {
        anchor: anchor === selectionStart ? newSelectionStart : newSelectionEnd,
        focus: anchor === selectionStart ? newSelectionEnd : newSelectionStart,
    });
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('convertBlocksToLists', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
